/**
 * Portal Products Page
 * Product management for PFLOJ (Shop) users
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { PermissionGate } from '../../components/PermissionGate';
import { resolveImage } from '../../utils/image';
import portalService, {
  getProductDigital, uploadDigitalFile, attachDigitalFile, detachDigitalFile,
} from '../../services/portal.service';
import api from '../../services/api';
import type { PortalProduct, PortalProductCategory } from '../../types/portal.types';

interface ProductFormData {
  NOME: string;
  DESCRICAO: string;
  PRECO: number;
  PRECO_PROMOCIONAL?: number;
  CATEGORIA_ID?: string;
  STOCK: number;
  ATIVO: boolean;
  IVA: number;
  imagem?: string;
  mimeType?: string;
}

interface DigitalFileInfo {
  nomeFicheiro: string;
  ficheiroUrl: string;
  storagePath?: string;
  formato?: string;
  tamanhoMb?: number;
}

const PortalProducts: React.FC = () => {
  const { hasPermission } = useAuth();
  const [products, setProducts] = useState<PortalProduct[]>([]);
  const [categories, setCategories] = useState<PortalProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<PortalProduct | null>(null);
  const [digitalFile, setDigitalFile] = useState<File | null>(null);
  const [existingDigital, setExistingDigital] = useState<DigitalFileInfo | null>(null);
  const [uploadingDigital, setUploadingDigital] = useState(false);
  const [grantModal, setGrantModal] = useState<{ produtoId: string; produtoNome: string } | null>(null);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantNome, setGrantNome] = useState('');
  const [granting, setGranting] = useState(false);
  const [formData, setFormData] = useState<ProductFormData>({
    NOME: '',
    DESCRICAO: '',
    PRECO: 0,
    STOCK: 0,
    ATIVO: true,
    IVA: 0,
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [productsRes, categoriesRes] = await Promise.all([
        portalService.getProducts(),
        portalService.getProductCategories(),
      ]);
      setProducts(productsRes.data || []);
      setCategories(categoriesRes || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleGrant = async () => {
    if (!grantModal || !grantEmail) return;
    setGranting(true);
    try {
      const res = await api.post('/portal/digital/grant', {
        produtoId: grantModal.produtoId,
        emailCliente: grantEmail,
        nomeCliente: grantNome || undefined,
      });
      if (res.data?.success) {
        alert(`Acesso concedido e email enviado para ${grantEmail}`);
        setGrantModal(null);
        setGrantEmail('');
        setGrantNome('');
      } else {
        alert(res.data?.error || 'Erro ao conceder acesso');
      }
    } catch (err: any) {
      alert(err?.response?.data?.error || err.message || 'Erro ao conceder acesso');
    } finally {
      setGranting(false);
    }
  };

  const isDigitalCategory = (): boolean => {
    if (!formData.CATEGORIA_ID) return false;
    const cat = categories.find(c => c.ID === formData.CATEGORIA_ID);
    return !!(cat && cat.NOME.toLowerCase().includes('digital'));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        PRECO: formData.PRECO || 0,
        PRECO_PROMOCIONAL: formData.PRECO_PROMOCIONAL || undefined,
      };
      let savedId: string;
      if (editingProduct) {
        await portalService.updateProduct(editingProduct.ID_PRODUTO, dataToSend);
        savedId = editingProduct.ID_PRODUTO;
      } else {
        const created = await portalService.createProduct(dataToSend);
        savedId = (created as any).ID || (created as any).ID_PRODUTO;
      }

      // Upload digital file if category is digital and a new file was selected
      if (isDigitalCategory() && digitalFile && savedId) {
        setUploadingDigital(true);
        try {
          const uploadResult = await uploadDigitalFile(savedId, digitalFile);
          await attachDigitalFile(savedId, {
            ficheiroUrl:  uploadResult.ficheiroUrl,
            storagePath:  uploadResult.storagePath,
            nomeFicheiro: uploadResult.nomeFicheiro,
            formato:      uploadResult.formato,
            tamanhoMb:    uploadResult.tamanhoMb,
          });
        } finally {
          setUploadingDigital(false);
        }
      }

      setShowModal(false);
      setEditingProduct(null);
      resetForm();
      loadData();
    } catch (err: any) {
      setError(err.message || 'Erro ao guardar produto');
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      setFormData({ ...formData, imagem: base64, mimeType: file.type });
    };
    reader.readAsDataURL(file);
  };

  const handleEdit = async (product: PortalProduct) => {
    setEditingProduct(product);
    setDigitalFile(null);
    setExistingDigital(null);
    setFormData({
      NOME: product.NOME,
      DESCRICAO: product.DESCRICAO || '',
      PRECO: product.PRECO || 0,
      PRECO_PROMOCIONAL: product.PRECO_PROMOCIONAL || undefined,
      CATEGORIA_ID: product.CATEGORIA_ID,
      STOCK: product.STOCK,
      ATIVO: product.ATIVO,
      IVA: product.IVA ?? 0,
      imagem: product.IMAGEM,
      mimeType: product.MIME_TYPE,
    });
    setShowModal(true);
    // Load existing digital file info if category is digital
    try {
      const digital = await getProductDigital(product.ID_PRODUTO);
      if (digital) setExistingDigital(digital);
    } catch { /* ignore */ }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja eliminar este produto?')) return;
    try {
      await portalService.deleteProduct(id);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Erro ao eliminar produto');
    }
  };

  const resetForm = () => {
    setFormData({ NOME: '', DESCRICAO: '', PRECO: 0, STOCK: 0, ATIVO: true, IVA: 0 });
    setDigitalFile(null);
    setExistingDigital(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pagali-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Meus Produtos</h1>
          <p className="text-gray-600">Gerir o catalogo de produtos da sua loja</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingProduct(null);
            setShowModal(true);
          }}
          className="px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Novo Produto
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button onClick={() => setError(null)} className="text-sm text-red-500 underline mt-1">
            Fechar
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total Produtos</p>
          <p className="text-2xl font-semibold">{products.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Ativos</p>
          <p className="text-2xl font-semibold text-green-600">
            {products.filter((p) => p.ATIVO).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Inativos</p>
          <p className="text-2xl font-semibold text-gray-400">
            {products.filter((p) => !p.ATIVO).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Stock Baixo</p>
          <p className="text-2xl font-semibold text-orange-500">
            {products.filter((p) => p.STOCK < 10).length}
          </p>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Produto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Categoria
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preco
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acoes
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    Nenhum produto encontrado. Clique em "Novo Produto" para adicionar.
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.ID_PRODUTO} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {resolveImage(product.IMAGEM, product.MIME_TYPE) ? (
                          <img
                            src={resolveImage(product.IMAGEM, product.MIME_TYPE)!}
                            alt={product.NOME}
                            className="h-10 w-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{product.NOME}</div>
                          {product.DESCRICAO && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {product.DESCRICAO}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.CATEGORIA_NOME || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {(product.PRECO || 0).toLocaleString('pt-CV')} ECV
                      </div>
                      {product.PRECO_PROMOCIONAL && (
                        <div className="text-sm text-green-600">
                          Promo: {product.PRECO_PROMOCIONAL.toLocaleString('pt-CV')} ECV
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${product.STOCK < 10 ? 'text-orange-600' : 'text-gray-900'}`}>
                        {product.STOCK}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        product.ATIVO
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {product.ATIVO ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(product)}
                          className="text-pagali-primary hover:text-pagali-primary/80 mr-3"
                        >
                          Editar
                        </button>
                        {(product as any).TIPO === 'DIGITAL' && (
                          <button
                            onClick={() => { setGrantModal({ produtoId: product.ID_PRODUTO, produtoNome: product.NOME }); setGrantEmail(''); setGrantNome(''); }}
                            className="text-purple-600 hover:text-purple-900 mr-3"
                            title="Conceder acesso digital a um cliente"
                          >
                            Acesso
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(product.ID_PRODUTO)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Eliminar
                        </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">
                {editingProduct ? 'Editar Produto' : 'Novo Produto'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input
                  type="text"
                  required
                  value={formData.NOME}
                  onChange={(e) => setFormData({ ...formData, NOME: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descricao</label>
                <textarea
                  value={formData.DESCRICAO}
                  onChange={(e) => setFormData({ ...formData, DESCRICAO: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preco (CVE) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.PRECO}
                    onChange={(e) => setFormData({ ...formData, PRECO: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preco Promocional</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.PRECO_PROMOCIONAL || ''}
                    onChange={(e) => setFormData({ ...formData, PRECO_PROMOCIONAL: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                  <select
                    value={formData.CATEGORIA_ID || ''}
                    onChange={(e) => setFormData({ ...formData, CATEGORIA_ID: e.target.value || undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  >
                    <option value="">Sem categoria</option>
                    {categories.map((cat) => (
                      <option key={cat.ID} value={cat.ID}>{cat.NOME}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stock *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.STOCK}
                    onChange={(e) => setFormData({ ...formData, STOCK: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IVA</label>
                <div className="flex gap-2 flex-wrap">
                  {[0, 8, 15].map((rate) => (
                    <button
                      key={rate}
                      type="button"
                      onClick={() => setFormData({ ...formData, IVA: rate })}
                      className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        formData.IVA === rate
                          ? 'bg-pagali-primary text-white border-pagali-primary'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {rate === 0 ? 'Isento (0%)' : `${rate}%`}
                    </button>
                  ))}
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      placeholder="Outro %"
                      value={![0, 8, 15].includes(formData.IVA) ? formData.IVA : ''}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (!isNaN(v)) setFormData({ ...formData, IVA: v });
                      }}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Imagem do Produto</label>
                <div className="flex items-center gap-4">
                  {resolveImage(formData.imagem, formData.mimeType) && (
                    <img
                      src={resolveImage(formData.imagem, formData.mimeType)!}
                      alt="Preview"
                      className="h-16 w-16 rounded-lg object-cover border border-gray-200"
                    />
                  )}
                  <label className="cursor-pointer px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm text-gray-700">
                    {formData.imagem ? 'Alterar Foto' : 'Carregar Foto'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                  {formData.imagem && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, imagem: undefined, mimeType: undefined })}
                      className="text-sm text-red-500 hover:text-red-700"
                    >
                      Remover
                    </button>
                  )}
                </div>
              </div>
              {/* Digital file upload — only shown when category is "Digital" */}
              {isDigitalCategory() && (
                <div className="border border-pagali-primary/30 rounded-lg p-4 bg-pagali-primary/5">
                  <label className="block text-sm font-medium text-pagali-primary mb-2">
                    Ficheiro Digital (PDF, ZIP, EPUB…)
                  </label>
                  {existingDigital && !digitalFile && (
                    <div className="flex items-center gap-3 mb-3 p-2 bg-white rounded border border-gray-200 text-sm">
                      <svg className="w-5 h-5 text-pagali-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="flex-1 truncate text-gray-700">
                        {existingDigital.nomeFicheiro || 'Ficheiro carregado'}
                        {existingDigital.tamanhoMb && (
                          <span className="text-gray-400 ml-2">({existingDigital.tamanhoMb} MB)</span>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={async () => {
                          if (editingProduct && window.confirm('Remover ficheiro digital?')) {
                            await detachDigitalFile(editingProduct.ID_PRODUTO).catch(() => {});
                            setExistingDigital(null);
                          }
                        }}
                        className="text-red-500 hover:text-red-700 text-xs"
                      >
                        Remover
                      </button>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <label className="cursor-pointer px-4 py-2 border border-pagali-primary/50 text-pagali-primary rounded-lg hover:bg-pagali-primary/10 text-sm">
                      {digitalFile ? digitalFile.name : (existingDigital ? 'Substituir Ficheiro' : 'Carregar Ficheiro')}
                      <input
                        type="file"
                        accept=".pdf,.zip,.epub,.mp4,.mov"
                        onChange={(e) => setDigitalFile(e.target.files?.[0] || null)}
                        className="hidden"
                      />
                    </label>
                    {digitalFile && (
                      <button
                        type="button"
                        onClick={() => setDigitalFile(null)}
                        className="text-sm text-red-500 hover:text-red-700"
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                  {uploadingDigital && (
                    <p className="text-xs text-pagali-primary mt-2 flex items-center gap-1">
                      <span className="animate-spin inline-block w-3 h-3 border border-pagali-primary border-t-transparent rounded-full"></span>
                      A carregar ficheiro…
                    </p>
                  )}
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="ativo"
                  checked={formData.ATIVO}
                  onChange={(e) => setFormData({ ...formData, ATIVO: e.target.checked })}
                  className="h-4 w-4 text-pagali-primary focus:ring-pagali-primary border-gray-300 rounded"
                />
                <label htmlFor="ativo" className="ml-2 block text-sm text-gray-900">
                  Produto ativo
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80"
                >
                  {editingProduct ? 'Guardar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Conceder Acesso Digital */}
      {grantModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Conceder Acesso Digital</h2>
            <p className="text-sm text-gray-500 mb-4">
              Produto: <span className="font-medium text-gray-700">{grantModal.produtoNome}</span>
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email do cliente *</label>
                <input
                  type="email"
                  value={grantEmail}
                  onChange={e => setGrantEmail(e.target.value)}
                  placeholder="cliente@exemplo.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome do cliente (opcional)</label>
                <input
                  type="text"
                  value={grantNome}
                  onChange={e => setGrantNome(e.target.value)}
                  placeholder="Nome completo"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setGrantModal(null)}
                className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleGrant}
                disabled={!grantEmail || granting}
                className="flex-1 py-2 bg-pagali-purple text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {granting ? 'A enviar...' : 'Conceder e Enviar Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalProducts;
